<template>
  <div
    :class="['modal', 'fade', { show: isVisible }]"
    data-bs-backdrop="static"
    data-bs-keyboard="false"
    id="confirmModal"
    tabindex="-1"
    aria-labelledby="confirmModalLabel"
    aria-hidden="true"
    role="dialog"
    @click.self="confirmNo"
  >
    <div class="modal-dialog modal-dialog-centered">
      <div class="modal-content">
        <div class="modal-header">
          <h5
            class="modal-title d-flex justify-content-start align-items-center"
            id="confirmModalLabel"
          >
            <i class="bi bi-shield-check text-success fs-2"></i> Confirmation
          </h5>
          <button
            type="button"
            class="btn-close"
            aria-label="Close"
            @click="confirmNo"
          ></button>
        </div>
        <div class="modal-body">
          {{ message }}
        </div>
        <div class="modal-footer">
          <button
            type="button"
            class="btn btn-primary"
            ref="yesButton"
            @click="confirmYes"
          >
            Yes
          </button>
          <button type="button" class="btn btn-secondary" @click="confirmNo">No</button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { computed, ref, watch, nextTick } from "vue";
import store from "@/store";
export default {
  setup() {
    const yesButton = ref(null); // Reference to the "Yes" button

    // Computed properties to access Vuex store state
    const isVisible = computed(() => store.state.confirmModal.isVisible);
    const message = computed(() => store.state.confirmModal.message);

    // Watch for modal visibility changes
    watch(isVisible, (newValue) => {
      if (newValue) {
        // Focus the "Yes" button when the modal becomes visible
        nextTick(() => {
          yesButton.value?.focus();
        });
      }
    });

    // Methods to dispatch Vuex actions
    const confirmYes = () => {
      store.dispatch("confirmYes");
    };

    const confirmNo = () => {
      store.dispatch("confirmNo");
    };

    return {
      isVisible,
      message,
      confirmYes,
      confirmNo,
      yesButton,
    };
  },
};
</script>

<style scoped>
.modal.show {
  display: block;
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.7);
  backdrop-filter: blur(8px);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 99999999;
  opacity: 1;
  visibility: visible;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}

.modal.fade:not(.show) {
  opacity: 0;
  visibility: hidden;
}

.modal-dialog {
  max-width: 500px;
  width: 90%;
  margin: 1rem;
}

.modal-content {
  background: linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%);
  border-radius: 20px;
  border: none;
  box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.25), 0 0 0 1px rgba(255, 255, 255, 0.1),
    0 0 80px rgba(163, 14, 14, 0.15);
  overflow: hidden;
  transform: scale(0.9);
  transition: all 0.4s cubic-bezier(0.34, 1.56, 0.64, 1);
}

.modal.show .modal-content {
  transform: scale(1);
}

.modal-header {
  background: linear-gradient(135deg, #a30e0e 0%, #8a0b0b 100%);
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
  padding: 1.5rem 2rem;
  position: relative;
  overflow: hidden;
}

.modal-header::before {
  content: "";
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  height: 2px;
  background: linear-gradient(
    90deg,
    transparent 0%,
    rgba(255, 255, 255, 0.6) 50%,
    transparent 100%
  );
  animation: shimmer 3s ease-in-out infinite;
}

.modal-title {
  color: white;
  font-size: 1.5rem;
  font-weight: 700;
  text-shadow: 0 2px 4px rgba(0, 0, 0, 0.3);
  margin: 0;
  gap: 1rem;
}

.modal-title i {
  width: 50px;
  height: 50px;
  background: rgba(255, 255, 255, 0.2);
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  backdrop-filter: blur(10px);
  border: 1px solid rgba(255, 255, 255, 0.3);
  animation: pulse 2s ease-in-out infinite;
}

.btn-close {
  background: rgba(255, 255, 255, 0.2);
  border: 1px solid rgba(255, 255, 255, 0.3);
  border-radius: 8px;
  width: 36px;
  height: 36px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-size: 1rem;
  cursor: pointer;
  transition: all 0.3s ease;
  backdrop-filter: blur(10px);
  opacity: 1;
  padding: 0;
}

.btn-close:hover {
  background: rgba(255, 255, 255, 0.3);
  transform: scale(1.1) rotate(90deg);
  color: white;
}

.modal-body {
  padding: 2rem;
  font-size: 1.1rem;
  color: #2d3748;
  line-height: 1.6;
  text-align: center;
  background: radial-gradient(
      circle at 20% 80%,
      rgba(163, 14, 14, 0.03) 0%,
      transparent 50%
    ),
    radial-gradient(circle at 80% 20%, rgba(163, 14, 14, 0.03) 0%, transparent 50%);
}

.modal-footer {
  background: #f8f9fa;
  padding: 1.5rem 2rem;
  border-top: 1px solid #e9ecef;
  border-bottom-left-radius: 20px;
  border-bottom-right-radius: 20px;
  display: flex;
  gap: 1rem;
  justify-content: center;
}

.btn {
  padding: 0.75rem 2rem;
  border: none;
  border-radius: 10px;
  font-weight: 600;
  font-size: 1rem;
  cursor: pointer;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  min-width: 100px;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
}

.btn-primary {
  background: linear-gradient(135deg, #a30e0e 0%, #8a0b0b 100%);
  color: white;
}

.btn-primary:hover {
  background: linear-gradient(135deg, #8a0b0b 0%, #700000 100%);
  transform: translateY(-2px);
  box-shadow: 0 4px 15px rgba(163, 14, 14, 0.3);
}

.btn-primary:focus {
  background: linear-gradient(135deg, #8a0b0b 0%, #700000 100%);
  box-shadow: 0 0 0 3px rgba(163, 14, 14, 0.3);
}

.btn-secondary {
  background: linear-gradient(135deg, #6c757d, #5a6268);
  color: white;
}

.btn-secondary:hover {
  background: linear-gradient(135deg, #5a6268, #495057);
  transform: translateY(-2px);
  box-shadow: 0 4px 15px rgba(108, 117, 125, 0.3);
}

.btn-secondary:focus {
  background: linear-gradient(135deg, #5a6268, #495057);
  box-shadow: 0 0 0 3px rgba(108, 117, 125, 0.3);
}

.btn:active {
  transform: translateY(0);
}

/* Animations */
@keyframes shimmer {
  0%,
  100% {
    transform: translateX(-100%);
  }
  50% {
    transform: translateX(100%);
  }
}

@keyframes pulse {
  0%,
  100% {
    transform: scale(1);
    box-shadow: 0 0 0 0 rgba(255, 255, 255, 0.4);
  }
  50% {
    transform: scale(1.05);
    box-shadow: 0 0 0 10px rgba(255, 255, 255, 0);
  }
}

/* Responsive Design */
@media (max-width: 768px) {
  .modal-dialog {
    width: 95%;
    margin: 0.5rem;
  }

  .modal-header {
    padding: 1.25rem 1.5rem;
  }

  .modal-body {
    padding: 1.5rem;
    font-size: 1rem;
  }

  .modal-footer {
    padding: 1.25rem 1.5rem;
    flex-direction: column;
  }

  .btn {
    width: 100%;
    padding: 1rem 2rem;
  }

  .modal-title {
    font-size: 1.25rem;
  }

  .modal-title i {
    width: 40px;
    height: 40px;
    font-size: 1.25rem;
  }
}

@media (max-width: 480px) {
  .modal-header {
    padding: 1rem 1.25rem;
    flex-direction: column;
    text-align: center;
    gap: 1rem;
  }

  .modal-title {
    flex-direction: column;
    gap: 0.5rem;
  }

  .modal-body {
    padding: 1.25rem;
  }

  .modal-footer {
    padding: 1rem 1.25rem;
  }
}

/* Focus styles for accessibility */
.btn:focus {
  outline: 2px solid rgba(163, 14, 14, 0.5);
  outline-offset: 2px;
}

.btn-close:focus {
  outline: 2px solid rgba(255, 255, 255, 0.5);
  outline-offset: 2px;
}
</style>
